import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  CardContent,
  Grid,
  IconButton,
  Paper,
  TextField,
  Typography,
  Tooltip,
  useTheme,
  alpha,
  Divider,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Alert
} from '@mui/material';
import {
  ContentCopy,
  CheckCircle,
  HelpOutline,
  Campaign,
  ArrowForward,
  Info
} from '@mui/icons-material';
import { useDispatch } from 'react-redux';
import { showSnackBar } from '../../store/compatibility';
import { BASE_URL } from '../../../config/config';
import Title from '../settings/Title';
import style from '../settings/settings.module.scss';
import APIKit from '../../../config/APIKit';

export default function GoogleAdsIntegration() {
  const theme = useTheme();
  const dispatch = useDispatch();
  const [activeCrm, setActiveCrm] = useState(null);
  const [loading, setLoading] = useState(true);

  const activeCrmId = localStorage.getItem('activeCrmId');
  const webhookUrl = activeCrmId ? `${BASE_URL}/webhooks/google-ads/${activeCrmId}` : '';

  useEffect(() => {
    const fetchActiveCrm = async () => {
      if (!activeCrmId) {
        setLoading(false);
        return;
      }
      try {
        const res = await APIKit.get('/crms');
        if (res.status && res.data) {
          const active = res.data.find(c => c.id === activeCrmId);
          setActiveCrm(active || null);
        }
      } catch (error) {
        console.error("Failed to load workspace detail", error);
      } finally {
        setLoading(false);
      }
    };
    fetchActiveCrm();
  }, [activeCrmId]);

  const handleCopy = () => {
    if (!webhookUrl) return;
    navigator.clipboard.writeText(webhookUrl);
    dispatch(showSnackBar({
      show: true,
      variant: 'success',
      message: 'Google Ads Webhook URL copied to clipboard!'
    }));
  };

  const steps = [
    {
      label: 'Locate Lead Form Settings in Google Ads',
      description: 'Sign in to your Google Ads account. In the page menu on the left, click "Assets" or "Ads & assets", and select "Lead forms". Click the blue "+" button to create a new lead form or click an existing one to edit.',
    },
    {
      label: 'Set Up Lead Delivery Option',
      description: 'Scroll down to the "Lead delivery option" section. Tick or select the option to receive leads via Webhook.',
    },
    {
      label: 'Enter Webhook Details',
      description: `Copy the unique Webhook URL shown below and paste it into Google Ads. For the "Key" field, you can enter any string (e.g. "yorocrm_verify_key"). Our CRM routes leads securely using the workspace identifier in the path.`,
    },
    {
      label: 'Send Test Data & Save',
      description: 'Click "Send test data" in Google Ads to verify the connection. Once verified successfully (showing "Test data sent successfully"), save your lead form.',
    },
  ];

  if (loading) {
    return (
      <div className={style.container}>
        <Title title="Google Ads Lead Integration" enableButton={false} />
        <Paper
          sx={{
            p: 6,
            textAlign: "center",
            borderRadius: "20px",
            background: "linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)",
            boxShadow: "0 4px 20px rgba(0, 0, 0, 0.08)",
          }}
        >
          <Typography>Loading details...</Typography>
        </Paper>
      </div>
    );
  }

  return (
    <div className={style.container}>
      <Title title="Google Ads Lead Integration" enableButton={false} />

      <div className={style.content} style={{ flexDirection: 'column', gap: '24px' }}>
        {!activeCrmId ? (
          <Alert severity="warning" sx={{ borderRadius: '16px', border: `1px solid ${theme.palette.warning.main}` }}>
            No active CRM workspace selected. Please select a workspace from the Topbar to view your Google Ads webhook URL.
          </Alert>
        ) : (
          <Grid container spacing={3}>
            {/* Left Column: Webhook Details */}
            <Grid size={{ xs: 12, md: 6 }}>
              <Paper
                sx={{
                  p: 4,
                  borderRadius: "20px",
                  border: "1px solid rgba(0, 0, 0, 0.08)",
                  background: "linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)",
                  boxShadow: "0 8px 32px rgba(0, 0, 0, 0.05)",
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 3
                }}
              >
                <Box display="flex" alignItems="center" gap={2}>
                  <Box
                    sx={{
                      p: 1.5,
                      borderRadius: "12px",
                      background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.12)} 0%, ${alpha(theme.palette.secondary.main, 0.12)} 100%)`,
                      color: theme.palette.primary.main
                    }}
                  >
                    <Campaign />
                  </Box>
                  <Box>
                    <Typography variant="h6" fontWeight={700}>
                      Webhook Configuration
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Scope: {activeCrm ? activeCrm.name : 'Active Workspace'}
                    </Typography>
                  </Box>
                </Box>

                <Divider sx={{ my: 1 }} />

                <Typography variant="body2" color="text.secondary">
                  Use this webhook URL inside Google Ads Manager to instantly push captured leads into your current CRM workspace.
                </Typography>

                <Box display="flex" alignItems="center" gap={1}>
                  <TextField
                    fullWidth
                    label="Google Ads Webhook URL"
                    value={webhookUrl}
                    slotProps={{
                      input: {
                        readOnly: true,
                        style: { fontFamily: 'monospace', fontSize: '13px' }
                      }
                    }}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "12px",
                        bgcolor: 'rgba(0, 0, 0, 0.015)'
                      }
                    }}
                  />
                  <Tooltip title="Copy Webhook URL" arrow>
                    <IconButton
                      onClick={handleCopy}
                      color="primary"
                      sx={{
                        p: 1.8,
                        borderRadius: "12px",
                        border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                        bgcolor: alpha(theme.palette.primary.main, 0.04),
                        "&:hover": {
                          bgcolor: alpha(theme.palette.primary.main, 0.08)
                        }
                      }}
                    >
                      <ContentCopy />
                    </IconButton>
                  </Tooltip>
                </Box>

                <Box
                  sx={{
                    p: 2,
                    borderRadius: '12px',
                    bgcolor: alpha(theme.palette.info.main, 0.06),
                    border: `1px solid ${alpha(theme.palette.info.main, 0.15)}`,
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 1.5,
                    mt: 'auto'
                  }}
                >
                  <Info color="info" fontSize="small" sx={{ mt: 0.2 }} />
                  <Typography variant="caption" color="text.secondary" style={{ lineHeight: 1.5 }}>
                    <strong>Note:</strong> Leads received via this URL are automatically processed, parsed, and created as new contacts with the source <strong>"Google Ads"</strong>, tagged specifically to your active workspace.
                  </Typography>
                </Box>
              </Paper>
            </Grid>

            {/* Right Column: Setup Walkthrough */}
            <Grid size={{ xs: 12, md: 6 }}>
              <Paper
                sx={{
                  p: 4,
                  borderRadius: "20px",
                  border: "1px solid rgba(0, 0, 0, 0.08)",
                  background: "linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)",
                  boxShadow: "0 8px 32px rgba(0, 0, 0, 0.05)"
                }}
              >
                <Typography variant="h6" fontWeight={700} mb={3}>
                  Setup Step-by-Step Guide
                </Typography>

                <Stepper orientation="vertical" connector={<ArrowForward style={{ display: 'none' }} />}>
                  {steps.map((step, index) => (
                    <Step key={step.label} active={true}>
                      <StepLabel
                        StepIconComponent={() => (
                          <Box
                            sx={{
                              width: 28,
                              height: 28,
                              borderRadius: '50%',
                              bgcolor: alpha(theme.palette.primary.main, 0.1),
                              color: theme.palette.primary.main,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontWeight: 700,
                              fontSize: '13px'
                            }}
                          >
                            {index + 1}
                          </Box>
                        )}
                      >
                        <Typography variant="subtitle2" fontWeight={600}>
                          {step.label}
                        </Typography>
                      </StepLabel>
                      <StepContent>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2, mt: 0.5, pl: 0.5 }}>
                          {step.description}
                        </Typography>
                      </StepContent>
                    </Step>
                  ))}
                </Stepper>
              </Paper>
            </Grid>
          </Grid>
        )}
      </div>
    </div>
  );
}
